import * as CL from '@design-system/component-library';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  billingAccountMsg,
  billingFrequencyMsg,
  contractEndDateMsg,
  durationOfContractMsg,
  idNumberMsg,
  listPriceMsg,
  monthsMsg,
  notAvailableMsg,
  statusMsg,
  t,
} from '../../common/i18n';
import { formatTimestampToUTCDDMMYYYY } from '../../common/utils/dateUtils';
import { getRecurringPriceAsText } from '../../common/utils/priceUtils';
import { getSubscriptionStatus } from '../../common/utils/subscriptionUtils.js';
import { SubscriptionType } from '../../generated/api/subscriptionType.js';
import { openDialog } from '../../public/site/dialogStore.js';
import { DetailsWrapper } from '../DetailsWrapper';
import { getCommitmentText } from '../EoeSubscriptionDetails/eppSubscriptionUtils';
import { Loading } from '../Loading';
import {
  SubscriptionDetailsAccordions,
  SubscriptionDetailsBillingAccount,
} from '../SubscriptionDetails/SubscriptionDetails.js';
import { ButtonGroup, SubscriptionDetailsButtonType } from '../SubscriptionDetails/subscriptionDetailsButtons.js';
import { getSubscriptionStatusFieldCL } from '../SubscriptionDetails/subscriptionDetailsCommon';
import { getLicensePricePerUnit } from '../SubscriptionDetails/subscriptionDetailsSoftwareProduct.js';
import type { CLDescriptionItem } from '../../common/types/componentLibrary';
import type { DialogParams } from '../../common/types/dialog.js';
import type { SubscriptionStatus } from '../../common/types/subscription';
import type { Subscription } from '../../generated/api/subscription.js';
import type { SubscriptionDetails } from '../../generated/api/subscriptionDetails';
import type { SubscriptionDetailsSoftwareProduct } from '../../generated/api/subscriptionDetailsSoftwareProduct';
import type { SubscriptionDetailsProps } from '../SubscriptionDetails/SubscriptionDetails.js';

interface SubscriptionDetailsSoftwareProductProps {
  subscription: Subscription;
  subscriptionStatus: SubscriptionStatus;
  details: SubscriptionDetails;
  softwareProductDetails?: SubscriptionDetailsSoftwareProduct;
}

const SoftwareProductDetails = ({
  subscription,
  subscriptionStatus,
  details,
  softwareProductDetails,
}: SubscriptionDetailsSoftwareProductProps) => {
  const items: CLDescriptionItem[] = [
    getSubscriptionStatusFieldCL(subscriptionStatus, t.ASQT(statusMsg)),
    {
      title: t.YTH3(idNumberMsg),
      description: subscription.subscriptionDisplayId,
    },
    {
      title: t.IFT9(billingAccountMsg),
      description: <SubscriptionDetailsBillingAccount subscription={subscription} />,
    },
    {
      title: t.CAA9(durationOfContractMsg),
      description: getCommitmentText(subscription),
    },
  ];
  const unitPrice = getLicensePricePerUnit(softwareProductDetails);
  if (unitPrice) {
    items.push({
      title: t.R39E('Unit price'),
      description: unitPrice,
    });
  }

  const billingPeriod = subscription.details?.billingPeriod;

  items.push({
    title: t.N68W(listPriceMsg),
    description: getRecurringPriceAsText(details.monthlyRecurringCharge, billingPeriod),
  });

  if (billingPeriod) {
    items.push({
      title: t.PE9O(billingFrequencyMsg),
      description: `${billingPeriod.toString()} ${t.XXVX(monthsMsg)}`,
    });
  }
  return <CL.Description items={items} />;
};

interface SubscriptionDetailsPbxProps {
  subscription: Subscription;
  subscriptionStatus: SubscriptionStatus;
  details: SubscriptionDetails;
}

const PbxDetails = ({ subscription, subscriptionStatus, details }: SubscriptionDetailsPbxProps) => (
  <CL.Description
    items={[
      getSubscriptionStatusFieldCL(subscriptionStatus, t.ASQT(statusMsg)),
      {
        title: t.YTH3(idNumberMsg),
        description: subscription.subscriptionDisplayId,
      },
      {
        title: t.IFT9(billingAccountMsg),
        description: <SubscriptionDetailsBillingAccount subscription={subscription} />,
      },
      {
        title: t.CAA9(durationOfContractMsg),
        description: details.commitmentPeriod
          ? `${details.commitmentPeriod} ${t.XXVX(monthsMsg)}`
          : t.NN5S(notAvailableMsg),
      },
      {
        title: t.LHJ4(contractEndDateMsg),
        description: details.commitmentEndDate
          ? `${formatTimestampToUTCDDMMYYYY(details.commitmentEndDate)}`
          : t.NN5S(notAvailableMsg),
      },
      {
        title: t.O5CK('Contractual price, VAT 0%'),
        description: getRecurringPriceAsText(details.monthlyRecurringCharge),
      },
    ]}
  />
);

export const SubscriptionService = ({
  breadCrumbs,
  companyInfo,
  hasPendingActions,
  subscription,
}: SubscriptionDetailsProps) => {
  const onShowDialog = (params: DialogParams) => openDialog(params);
  const subscriptionStatus = getSubscriptionStatus(subscription, hasPendingActions);
  const subscriptionId = subscription ? subscription.subscriptionId : '…';
  const companyName = companyInfo?.companyName;
  const { mdmdId } = useSearchParams<{ mdmdId: string }>();

  if (!subscription?.details) {
    return (
      <DetailsWrapper
        classes={['of-subscription-details']}
        detailsTop={breadCrumbs}
        heading={subscription?.subscriptionName || '…'}
        headingBottom={subscription?.details?.softwareProduct?.editionName || ''}
        headingTop=""
        heroPicto="generic-service-filled"
        id={`subscription-details-${subscriptionId}`}
      >
        <Loading />
      </DetailsWrapper>
    );
  }

  const getHeroPicto = (sub: Subscription) => {
    if (sub?.details?.mobilePbx && subscription.subscriptionType === SubscriptionType.MOBILE_PBX) {
      return 'pbx';
    }
    if (sub.details?.mobilePbx && subscription.subscriptionType === SubscriptionType.MOBILE_PBX_LITE) {
      return 'pbx-lite';
    }
    return 'generic-service-filled';
  };

  const pbxButtons = [SubscriptionDetailsButtonType.RING_SERVICE, SubscriptionDetailsButtonType.SUBMIT_SUPPORT_CASE];
  const buttonTypes =
    subscription?.details?.mobilePbx && subscription.subscriptionType === SubscriptionType.MOBILE_PBX
      ? pbxButtons
      : [SubscriptionDetailsButtonType.SUPPORT_REQUEST];

  const subscriptionDetails = (
    <div className="of-subscription-details__content of-subscription-details__content--service">
      {subscription?.details?.mobilePbx ? (
        <PbxDetails
          subscription={subscription}
          subscriptionStatus={subscriptionStatus}
          details={subscription.details}
        />
      ) : (
        <SoftwareProductDetails
          subscription={subscription}
          subscriptionStatus={subscriptionStatus}
          details={subscription.details}
          softwareProductDetails={subscription.details.softwareProduct}
        />
      )}
      <ButtonGroup
        buttonTypes={buttonTypes}
        subscription={subscription}
        hasPendingActions={hasPendingActions}
        onShowDialog={onShowDialog}
        mdmId={mdmdId}
      />
      <SubscriptionDetailsAccordions
        subscription={subscription}
        companyInfo={companyInfo}
        hasPendingActions={hasPendingActions}
      />
    </div>
  );

  return (
    <DetailsWrapper
      classes={['of-subscription-details']}
      detailsTop={breadCrumbs}
      heading={subscription.subscriptionName}
      headingBottom={
        `${companyName ?? ''}${companyName && subscription.details.softwareProduct?.editionName ? ` | ${subscription.details.softwareProduct.editionName}` : ''}` ||
        ''
      }
      headingTop=""
      heroPicto={getHeroPicto(subscription)}
      id={`subscription-details-${subscriptionId}`}
    >
      {subscriptionDetails}
    </DetailsWrapper>
  );
};
